import { useToast } from '@/hooks/useToast';
import { getBaseUrl } from '@/services/api';
import { getAuthToken } from '@/services/auth/getAuthToken';
import { downloadFromSource } from '@/utils/downloadUtils';
import { useCallback, useEffect, useRef, useState } from 'react';

interface FileDownloadOptions {
  filename: string;
  errorMessage?: string;
}

export const useFileDownload = () => {
  const { showToast } = useToast();
  const abortControllerReference = useRef<AbortController | undefined>(undefined);

  useEffect(() => {
    return () => {
      abortControllerReference.current?.abort();
    };
  }, []);

  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(
    async (path: string, options: FileDownloadOptions) => {
      setIsDownloading(true);

      const abortController = new AbortController();
      abortControllerReference.current = abortController;

      try {
        const authToken = await getAuthToken();
        const response = await fetch(`${getBaseUrl()}${path}`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${authToken}` },
          signal: abortController.signal,
        });

        if (response.status === 401) {
          localStorage.removeItem('authToken');
          globalThis.location.reload();
          return;
        }

        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`);
        }

        await downloadFromSource(response, { filename: options.filename });
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        showToast({
          message: options.errorMessage ?? 'Failed to download file',
          type: 'error',
        });
      } finally {
        abortControllerReference.current = undefined;
        setIsDownloading(false);
      }
    },
    [showToast]
  );

  return { download, isDownloading };
};
